import React from 'react';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  Divider,
  Container,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FilterForm from '../../components/organism/FilterForm';
import HeaderFiltro from './Header';
import PlaceCardList from '../../components/organism/PlaceCardList';
import AvailabilityRequestModal from '../../components/organism/AvailabilityRequestModal';
import Theme from '../../theme/Theme';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { LoginService } from '../../services/LoginService';

const Home: React.FC = () => {
  const [openFilter, setOpenFilter] = React.useState(false);
  const [sidebarExpanded, setSidebarExpanded] = React.useState(true);
  const [openAvailabilityModal, setOpenAvailabilityModal] = React.useState(false);
  const [espacosParaModal, setEspacosParaModal] = React.useState<any[]>([]);
  const [filterNamesForModal, setFilterNamesForModal] = React.useState({
    cidade: '',
    regiao: '',
    bairro: '',
    evento: '',
    espaco: '',
  });
  const resultsRef = React.useRef<HTMLDivElement>(null);

  const toggleFilter = () => setOpenFilter(!openFilter);
  const toggleSidebar = () => setSidebarExpanded(!sidebarExpanded);
  const handleOpenAvailabilityModal = () => setOpenAvailabilityModal(true);
  const handleCloseAvailabilityModal = () => setOpenAvailabilityModal(false);

  const handleViewResults = () => {
    // Fecha o drawer de filtros (mobile) e recolhe a sidebar (desktop)
    setOpenFilter(false);
    setSidebarExpanded(false);

    // Rola suavemente até a seção de resultados após recolher
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isUserMenuOpen = Boolean(userMenuAnchorEl);
  const isAuthenticated = LoginService.isAuthenticated();
  const userType = LoginService.getUserType();
  const shouldShowUserMenu = isAuthenticated && !!userType;
  const userRole = userType ?? 'UNKNOWN';

  const menuConfig = [
    {
      profiles: ['USER'],
      items: [
        {
          key: 'my-requests',
          label: 'Minhas solicitações',
          path: '/minhas-solicitacoes',
          icon: <ListAltOutlinedIcon fontSize="small" />,
        },
      ],
    },
    {
      profiles: ['ADMIN', 'CUSTOMER'],
      items: [
        {
          key: 'spaces-list',
          label: 'Listagem de espaços',
          path: '/listar/espaco',
          icon: <DashboardOutlinedIcon fontSize="small" />,
        },
        {
          key: 'open-requests',
          label: 'Solicitações abertas',
          path: '/solicitacoes-abertas',
          icon: <ListAltOutlinedIcon fontSize="small" />,
        },
      ],
    },
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchorEl(null);
  };

  const handleNavigateToPath = (path: string) => {
    handleCloseUserMenu();
    navigate(path);
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    LoginService.logout();
    navigate('/login');
  };

  const userMenuItems =
    menuConfig.find((config) => config.profiles.includes(userRole))?.items ?? [];

  return (
    <Box sx={{ bgcolor: '#F5F7FA', minHeight: '100vh' }}>
      {/* Header */}
      <HeaderFiltro />

      {/* Main Content */}
      <Box sx={{ display: 'flex', position: 'relative', minHeight: 'calc(100vh - auto)' }}>
        {isMobile ? (
          <>
            {/* Mobile Drawer for Filters */}
            <Drawer
              anchor="left"
              open={openFilter}
              onClose={toggleFilter}
              slotProps={{
                backdrop: {
                  sx: { zIndex: 9 },
                },
              }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: '100%',
                  maxWidth: isSmallMobile ? '100vw' : '85vw',
                  height: '100vh',
                  overflowY: 'auto',
                  bgcolor: '#F5F7FA',
                },
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'white',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  sticky: 'top',
                  zIndex: 10,
                }}
              >
                <Typography
                  variant={isSmallMobile ? 'body2' : 'h6'}
                  fontWeight="bold"
                  color="primary"
                >
                  Filtros de Busca
                </Typography>
                <Button onClick={toggleFilter} size="small" sx={{ minWidth: 'auto' }}>
                  <CloseIcon />
                </Button>
              </Box>
              <Box sx={{ p: isSmallMobile ? 1 : 2 }}>
                <FilterForm
                  open={openFilter}
                  toggleFilter={toggleFilter}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  onViewResults={handleViewResults}
                />
              </Box>
            </Drawer>
          </>
        ) : (
          <>
            {/* Desktop Sidebar */}
            <Box
              sx={{
                width: sidebarExpanded ? '340px' : '60px',
                bgcolor: '#F5F7FA',
                borderRight: '1px solid',
                borderColor: 'divider',
                height: 'calc(100vh - auto)',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'sticky',
                top: 0,
                pt: 2,
                transition: 'width 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: sidebarExpanded ? 'stretch' : 'center',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: '#D0D0D0',
                  borderRadius: '3px',
                  '&:hover': {
                    bgcolor: '#A0A0A0',
                  },
                },
              }}
            >
              {/* Toggle Button */}
              <Tooltip title={sidebarExpanded ? 'Recolher filtros' : 'Expandir filtros'}>
                <IconButton
                  onClick={toggleSidebar}
                  size="small"
                  sx={{
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider',
                    alignSelf: 'flex-end',
                    mr: 1,
                    mb: 2,
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'white',
                    },
                  }}
                >
                  {sidebarExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Tooltip>

              {sidebarExpanded && (
                <>
                  <Card
                    elevation={0}
                    sx={{
                      mx: 2,
                      mb: 2,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" fontWeight="bold" color="primary" mb={0.5}>
                        Filtros de Busca
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Personalize sua busca
                      </Typography>
                    </CardContent>
                  </Card>

                  <Box sx={{ px: 2, pb: 4 }}>
                    <FilterForm
                      open={openFilter}
                      toggleFilter={toggleFilter}
                      searchParams={searchParams}
                      setSearchParams={setSearchParams}
                      onViewResults={handleViewResults}
                    />
                  </Box>
                </>
              )}
            </Box>
          </>
        )}

        {/* Results Section */}
        <Box
          ref={resultsRef}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#F5F7FA',
          }}
        >
          {/* Top Bar with Filter Button and Results Info */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: isSmallMobile ? 1 : 2,
              bgcolor: 'white',
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
          >
            <Typography
              variant={isSmallMobile ? 'body2' : 'h6'}
              fontWeight="bold"
              color="text.secondary"
            >
              Espaços Disponíveis
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMobile && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={toggleFilter}
                  startIcon={<TuneIcon />}
                  size={isSmallMobile ? 'small' : 'medium'}
                >
                  Filtrar
                </Button>
              )}

              {shouldShowUserMenu && (
                <>
                  <Button
                    variant="outlined"
                    size={isSmallMobile ? 'small' : 'medium'}
                    startIcon={<PersonOutlineIcon />}
                    onClick={handleOpenUserMenu}
                    sx={{ textTransform: 'none' }}
                  >
                    Menu
                  </Button>
                  <Menu
                    anchorEl={userMenuAnchorEl}
                    open={isUserMenuOpen}
                    onClose={handleCloseUserMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {userMenuItems.map((item) => (
                      <MenuItem key={item.key} onClick={() => handleNavigateToPath(item.path)}>
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText>{item.label}</ListItemText>
                      </MenuItem>
                    ))}
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Sair</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Box>

          {/* Results Grid */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: isSmallMobile ? 1 : 2,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: '#D0D0D0',
                borderRadius: '4px',
                '&:hover': {
                  bgcolor: '#A0A0A0',
                },
              },
            }}
          >
            <Container maxWidth="xl" disableGutters>
              <PlaceCardList
                searchParams={searchParams}
                onUpdateModalData={(espacos: any[], filterNames: any) => {
                  setEspacosParaModal(espacos);
                  setFilterNamesForModal(filterNames);
                }}
              />
            </Container>
          </Box>
        </Box>
      </Box>

      {/* Botão flutuante para consultar disponibilidade */}
      <Box
        sx={{
          position: 'fixed',
          bottom: isSmallMobile ? 16 : 24,
          right: isSmallMobile ? 16 : 32,
          zIndex: 20,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<EventAvailableIcon />}
          size={isSmallMobile ? 'small' : 'medium'}
          onClick={handleOpenAvailabilityModal}
          sx={{
            borderRadius: 999,
            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
            px: isSmallMobile ? 2 : 3,
          }}
        >
          Consultar Interesse
        </Button>
      </Box>

      {/* Modal de Solicitação de Disponibilidade */}
      <AvailabilityRequestModal
        open={openAvailabilityModal}
        onClose={handleCloseAvailabilityModal}
        espacos={espacosParaModal}
        setSearchParams={setSearchParams}
        filterNames={filterNamesForModal}
        searchParams={searchParams}
      />
    </Box>
  );
};

export default Home;
